import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SectionCard, StatusChip, getModuleIcon } from '@platform/ui-kit';
import { isUnlimitedSeats } from '@platform/entitlements';
import { HelpPanel, type HelpContent } from '../portfolio_definition/HelpPanel';
import { api, type EntitlementRow } from '../../api';
import { useAsyncData } from './useAsyncData';

/**
 * The drawer carries what the screen's info banner used to say.
 *
 * That banner was removed on the owner's instruction (2026-10-07). The facts it held are
 * still the ones a client needs — what a seat is, where seats are assigned, and why the
 * numbers cannot be edited here — so they live in the drawer, which is where a question
 * gets looked up rather than being asserted at everyone on every visit.
 */
const LICENSE_USAGE_HELP: HelpContent = {
  title: 'License Usage',
  icon: '🔑',
  purpose:
    'Shows <strong>what this install is licensed for and how much of it is in use</strong>. A read-only view: the licence itself is set by Talogent, and this screen reports it.',
  doHere: [
    'See which modules are <strong>licensed</strong> for this install.',
    'Read each module&rsquo;s <strong>seat count</strong> and how many of those seats are taken.',
    'Spot a module that is <strong>licensed but has no seats free</strong> before trying to give someone access to it.',
  ],
  fits: 'Administration &rarr; License Usage',
  firstTime:
    'If someone cannot be given access to a module, look here first. Either the module is not licensed, or it is licensed with every seat already taken &mdash; the two look the same from the <em>Users</em> screen but are fixed in completely different ways.',
  sections: [
    {
      type: 'defs',
      label: 'What a seat is',
      items: [
        {
          term: 'A seat is a licence for one person',
          def: 'One named user holding access to one module occupies one seat in that module. Seats are per module, so the same person takes a seat in each module they can open.',
        },
        {
          term: '0 seats',
          def: 'None available. <strong>A module with 0 seats is unavailable even when it shows as licensed</strong> — this is the state that most often looks like a fault and is not one.',
        },
        {
          term: '9999 seats',
          def: 'Unlimited. The usage line reads <em>n in use &middot; unlimited</em> rather than showing a proportion, because there is nothing to be a proportion of.',
        },
      ],
    },
    {
      type: 'html',
      label: 'Where seats are actually assigned',
      html: 'Not here. A seat is taken when a person is given access to a module under <strong>Administration &rarr; Users</strong>, and released when that access is removed. So this screen is the denominator and <em>Users</em> is the numerator: if the count here looks wrong, the answer is in who has access, not in this screen.',
    },
    {
      type: 'html',
      label: 'Why nothing here is editable',
      html: 'The licence is <strong>commercial, not configuration</strong> — which modules an install has and how many seats each carries is part of the client&rsquo;s agreement with Talogent, so it is set on Talogent&rsquo;s side and reported here. <strong>To change a module or a seat count, contact Talogent.</strong> An administrator cannot grant their own install more licence than it has bought, which is the point of the screen being read-only rather than a limitation of it.',
    },
    {
      type: 'html',
      label: 'Licensed is not the same as visible',
      html: 'Two other things decide whether a person actually sees a module, and neither is a licence. <strong>Administration itself is not licensed</strong> — access to it comes from being an administrator. And a <strong>deactivated or locked account</strong> keeps its seat assignments but can open nothing, so a seat can read as in use by somebody who currently cannot sign in.',
    },
  ],
};

/**
 * License Usage — the customer's copy of Talogent's Entitlements & Licensing screen.
 *
 * A DUPLICATE, deliberately, not a shared component with a flag. The customer must
 * never reach the vendor's screen, and keeping them as separate files means the two
 * can diverge without one accidentally exposing the other's controls. The layout is
 * the same so the same numbers are read the same way on both sides.
 *
 * View only: there is no editing code here at all, so there is nothing to disable or
 * re-enable by mistake. The server refuses writes from a client domain regardless.
 */
export function LicenseUsageTab() {
  const { data, loading, error } = useAsyncData(() => api.admin.getEntitlements());

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  const rows = data?.entitlements ?? [];

  return (
    <Stack spacing={2}>
      <HelpPanel help={LICENSE_USAGE_HELP} />

      <SectionCard title="Licensed modules" disableContentPadding>
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              '& th': {
                textAlign: 'left',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.02em',
                color: 'text.secondary',
                px: 2.5,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                whiteSpace: 'nowrap',
              },
              '& td': {
                px: 2.5,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                verticalAlign: 'middle',
              },
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 72 }}>Licensed</th>
                <th>Module</th>
                <th style={{ width: 110 }}>Seats</th>
                <th style={{ width: 210 }}>Seat usage</th>

              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <LicenseUsageRow key={row.module_key} row={row} />
              ))}
            </tbody>
          </Box>
        </Box>
      </SectionCard>
    </Stack>
  );
}

function LicenseUsageRow({ row }: { row: EntitlementRow }) {
  const Icon = getModuleIcon(row.icon ?? '');
  const unlimited = isUnlimitedSeats(row.seat_limit);

  return (
    <tr>
      <td>
        <StatusChip
          tone={row.enabled ? 'success' : 'neutral'}
          label={row.enabled ? 'Yes' : 'No'}
        />
      </td>

      <td>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            aria-hidden
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: 'action.selected',
              color: row.available ? 'primary.main' : 'text.disabled',
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Typography variant="body2" fontWeight={500}>
            {row.name}
          </Typography>
        </Stack>
      </td>

      <td>
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {unlimited ? '∞' : row.seat_limit}
        </Typography>
      </td>

      <td>
        <SeatUsage
          limit={row.seat_limit}
          used={row.seats_used}
          available={row.seats_available}
          unlimited={unlimited}
          emptyLabel="No seats"
        />
      </td>

    </tr>
  );
}

/** Same usage cell as the vendor screen, so both sides read identically. */
function SeatUsage({
  limit,
  used,
  available,
  unlimited,
  emptyLabel,
}: {
  limit: number;
  used: number;
  available: number | null;
  unlimited: boolean;
  emptyLabel: string;
}) {
  if (unlimited) {
    return (
      <Typography variant="body2" color="text.secondary">
        {used} in use · unlimited
      </Typography>
    );
  }
  if (limit === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        {emptyLabel}
      </Typography>
    );
  }

  const percent = Math.min(100, (used / limit) * 100);
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {used} of {limit} used · {available} free
      </Typography>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={percent >= 100 ? 'error' : percent >= 80 ? 'warning' : 'primary'}
        sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
      />
    </Box>
  );
}
